import { APIStatus } from "@/api/APIStatus.type";
import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import Button from "@/components/Button";
import Spinner from "@/components/atoms/Spinner/Spinner";
import SBCFiltersSidebar from "@/components/organisms/SBCFiltersSidebar/SBCFiltersSidebar";
import HeaderBar from "@/components/organisms/SolutionView/HeaderBar";
import { PitchView } from "@/components/organisms/SolutionView/PitchView";
import SolutionCost from "@/components/organisms/SolutionView/SolutionCost";
import { SolutionSummary } from "@/components/organisms/SolutionView/SolutionSummary";
import { closeIcon, gearIcon } from "@/designSystemIcons";
import { useInitImport } from "@/hooks/useInitImport";
import { cn } from "@/lib/utils";
import { formatSbcSetName } from "@/pages/Sbcs/SbcSetPage/SbcSetPage";
import { setIsCurrentlyImporting } from "@/redux/globalState/globalStateSlice";
import {
  clearSbcParams,
  filtersActiveSelector,
  sbcFiltersSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import { Sbc, Solution, getSbcsSelector } from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { playerSwap } from "../../../api/privateApi";
import { NowImporting } from "../../molecules/NowImporting/NowImporting";
import { CompleteAndSolveAgain } from "./CompleteAndSolveAgain";

type SolutionViewProps = {
  solution: Solution;
  sbc: Sbc;
  sbcSetUrl: string;
  hybridSliderValue: number;
  useUntradeable: boolean;
  excludeGoldPlayers: boolean;
  excludeSilverPlayers: boolean;
  excludeBronzePlayers: boolean;
  excludeActiveSquad: boolean;
  excludeSpecialPlayers: boolean;
  excludeObjectivePlayers: boolean;
  excludeSbcPlayers: boolean;
  includeTransferlist: boolean;
  prioritizeDuplicates: boolean;
  showSwapView: boolean;
  toggleSwapView: (toggle: boolean) => void;
  onSwap: (player: PlayerSwap, index: number) => void;
  onHandleSolveAgain: () => void;
  onHandleCompleteAndRemovePlayers: () => void;
  handleUncompleteSBC: () => void;
};

const SIDEBAR_W = 340;

const SBCStatusOverlay = ({
  left,
  status,
  error,
  onHandleSolveAgain,
}: {
  left: string;
  status: APIStatus;
  error?: string;
  onHandleSolveAgain: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const hasFilters = useSelector(filtersActiveSelector);
  let children: React.ReactNode | null = null;

  if (status === "pending") {
    children = (
      <div className="flex items-center gap-x-3 touch-device:gap-x-4">
        <Oval stroke="#fff" strokeWidth={2} height={24} width={24} />
        <div className="text-white text-sm sm:text-base whitespace-nowrap">
          Generating unique SBC solution just for you
        </div>
      </div>
    );
  } else {
    children = (
      <div className="flex flex-col items-center">
        <div className="mb-8 text-lg touch-device:text-xl font-bold flex flex-row items-center gap-x-1">
          <p className="whitespace-nowrap">No solution found</p>
          <img
            className="w-4 h-4"
            src={
              import.meta.env.VITE_APP_ASSETS_BASE_URL +
              "/misc/error-symbol.png"
            }
          />
        </div>
        <p className="mb-20 text-sm text-dark-secondary px-2 py-1 bg-gray-700 rounded-lg min-w-72 max-w-80">
          {error}
        </p>
        <div className="flex flex-row justify-baseline gap-x-4">
          <Button
            onClick={onHandleSolveAgain}
            label="Solve Again"
            variant="primary"
          />
          <Button
            onClick={() => {
              dispatch(clearSbcParams());
            }}
            label={hasFilters ? "Clear Filters" : "No Filters"}
            variant={hasFilters ? "tertiary" : "disabled"}
            disabled={!hasFilters}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed z-30 top-5/12 -translate-x-1/2 -translate-y-1/2 flex px-4 touch-device:px-8 py-4 touch-device:py-6 items-center bg-panel-background border-3 border-panel-middleground rounded-lg"
      style={{ left }}
    >
      {children}
    </div>
  );
};

const SolutionView: React.FC<SolutionViewProps> = ({
  solution,
  sbc,
  sbcSetUrl,
  hybridSliderValue,
  useUntradeable,
  excludeGoldPlayers,
  excludeSilverPlayers,
  excludeBronzePlayers,
  excludeActiveSquad,
  excludeSpecialPlayers,
  excludeObjectivePlayers,
  excludeSbcPlayers,
  includeTransferlist,
  prioritizeDuplicates,
  onSwap,
  toggleSwapView,
  onHandleSolveAgain,
  onHandleCompleteAndRemovePlayers,
  handleUncompleteSBC,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedPlayer, setSelectedPlayer] = useState<number | undefined>();
  const { excludedPlayers } = useSelector(sbcFiltersSelector);
  const [selectedPlayerSwap, setSelectedPlayerSwap] = useState<
    number | undefined
  >();
  const pitchRef = useRef<HTMLDivElement>(null);
  const [playerSwaps, setPlayerSwaps] = useState([] as PlayerSwap[][]);
  const { user } = useSelector(getUserSelector);
  const initImport = useInitImport({ user });
  const { solutionStatus, solutionError } = useSelector(getSbcsSelector);
  const [loadingSwaps, setLoadingSwaps] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const smallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const [showFiltersBar, setShowFiltersBar] = useState(false);
  const [showImporting, setShowImporting] = useState(false);
  const showSidebar = !smallDesktop || showFiltersBar;
  const showDesktopSidebar = !smallDesktop && showSidebar;
  const overlayLeft = showDesktopSidebar
    ? `calc(50% - ${SIDEBAR_W / 2}px)`
    : "50%";
  const widthClass = isMobile ? "w-16" : smallDesktop ? "w-20" : "w-24";
  const navigate = useNavigate();

  const includedIds = useMemo(
    () => (solution?.players || []).map((p) => p.resource_id),
    [solution?.players],
  );
  const excludedIds = useMemo(
    () => (excludedPlayers || []).map((p) => p.resourceId),
    [excludedPlayers],
  );

  const playersToRender = useMemo(() => {
    if (solutionStatus === "pending") {
      return Array.from({ length: 11 }, (_, i) => ({
        placeholder: true,
        id: `ph-${i}`,
      }));
    } else if (solutionStatus === "fulfilled" && solution?.players) {
      return solution?.players;
    }
    return [];
  }, [solutionStatus, solution?.players]);

  useEffect(() => {
    if (!solution) return;
    setLoadingSwaps(true);
    const fetchPlayerSwaps = async () => {
      try {
        const res = await playerSwap(
          sbc?.id,
          hybridSliderValue * 2,
          useUntradeable,
          excludeGoldPlayers,
          excludeSilverPlayers,
          excludeBronzePlayers,
          excludeActiveSquad,
          excludeSpecialPlayers,
          excludeObjectivePlayers,
          excludeSbcPlayers,
          includeTransferlist,
          prioritizeDuplicates,
          includedIds,
          excludedIds,
        );
        setPlayerSwaps(res);
      } finally {
        setLoadingSwaps(false);
      }
    };
    fetchPlayerSwaps();
  }, [
    solution,
    user?.uuid,
    hybridSliderValue,
    sbc?.id,
    useUntradeable,
    excludeGoldPlayers,
    excludeSilverPlayers,
    excludeBronzePlayers,
    excludeActiveSquad,
    excludeSpecialPlayers,
    excludeObjectivePlayers,
    excludeSbcPlayers,
    includeTransferlist,
    prioritizeDuplicates,
    excludedPlayers,
  ]);

  const onSwapClicked = useCallback(
    (player: PlayerSwap, index: number) => {
      toggleSwapView(false);
      onSwap(player, index);
      setSelectedPlayer(undefined);
      setSelectedPlayerSwap(undefined);
    },
    [onSwap, toggleSwapView],
  );

  const handlePlayerClicked = useCallback((i: number) => {
    setSelectedPlayer((p) => (p === i ? undefined : i));
    setSelectedPlayerSwap(undefined);
  }, []);

  const handleSwapClicked = useCallback((i: number) => {
    setSelectedPlayerSwap((s) => (s === i ? undefined : i));
    setSelectedPlayer(undefined);
  }, []);

  return (
    <div
      className={cn(
        "w-full flex justify-center",
        isMobile && "overflow-hidden",
      )}
      style={{
        backgroundImage:
          "linear-gradient(158deg, rgba(120, 170, 191, 0.5) 0%, rgba(7, 49, 81, 0) 40%), linear-gradient(90deg, #1a2633 0%, #131b26 80%)",
      }}
    >
      <div className="flex justify-center w-full max-w-content">
        <div className="flex flex-col items-center flex-1 min-h-screen">
          <HeaderBar
            onBack={() =>
              navigate(
                window.location.pathname?.split("/")?.slice(0, -2)?.join("/"),
              )
            }
            title={formatSbcSetName(sbc?.name)}
            right={
              smallDesktop && (
                <button
                  className="flex items-center gap-3 mr-3"
                  onClick={() => setShowFiltersBar((v) => !v)}
                >
                  {gearIcon} <span>Settings</span>
                </button>
              )
            }
            sbcFiltersIsToggled={showFiltersBar}
            onToggleFilters={() => setShowFiltersBar((v) => !v)}
          />
          {(solutionStatus === "pending" || solutionStatus === "rejected") && (
            <SBCStatusOverlay
              left={overlayLeft}
              status={solutionStatus}
              error={solutionError}
              onHandleSolveAgain={onHandleSolveAgain}
            />
          )}
          <div className={cn(isMobile ? "h-0" : "h-3")} />
          {smallDesktop && showFiltersBar && (
            <div
              className={cn(
                "absolute right-0 z-[35] shrink-0",
                !isMobile ? "top-10" : "top-12 w-full",
                !isTouch && "top-12",
              )}
            >
              {!isMobile && showFiltersBar && !isTouch && (
                <div className="bg-panel-foreground h-12 flex items-center justify-between px-4">
                  <div className="flex items-center gap-2">
                    {gearIcon} <span>Settings</span>
                  </div>
                  <div
                    onClick={() => setShowFiltersBar((v) => !v)}
                    className="cursor-pointer"
                  >
                    {closeIcon}
                  </div>
                </div>
              )}
              <SBCFiltersSidebar
                showReward
                isMobile
                onHandleImportClicked={() =>
                  initImport().then(() => {
                    dispatch(setIsCurrentlyImporting(true));
                    window.open(
                      "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                      "_blank",
                    );
                    setTimeout(() => setShowImporting(true), 1000);
                  })
                }
                sbc={sbc}
                rewardImageUrl={sbcSetUrl}
                onHandleSolveAgain={onHandleSolveAgain}
                onHandleRemoveAndCompletePlayers={
                  onHandleCompleteAndRemovePlayers
                }
                isUsingClubImport={solution?.ownedPlayersCount > 0}
                isOnSolutionsViewPage
              />
              {showImporting && (
                <NowImporting
                  onClickDone={() => setShowImporting(false)}
                  onClickCancel={() => setShowImporting(false)}
                />
              )}
            </div>
          )}

          {!isMobile && (
            <div className="relative w-full max-w-content overflow-visible">
              <div className={cn("absolute top-30 left-1 z-20")}>
                <SolutionSummary
                  rating={solution?.rating}
                  chemistry={solution?.chem}
                  owned={solution?.ownedPlayersCount}
                  untradeables={solution?.untradeablesCount}
                  duplicates={solution?.duplicatesCount}
                />
              </div>
            </div>
          )}

          {isMobile && <SolutionCost solution={solution} isMobile={isMobile} />}
          {!isMobile && (
            <div className="w-full">
              <div className="flex items-center justify-center gap-4">
                <div className="flex-1 flex"></div>
                <div className="flex-1 flex">
                  <SolutionCost solution={solution} isMobile={isMobile} />
                </div>
                <div className="relative z-10 flex-1 flex">
                  <CompleteAndSolveAgain
                    isCompleted={sbc?.isCompleted}
                    isUsingClubImport={solution?.ownedPlayersCount > 0}
                    onHandleRemoveAndCompletePlayers={
                      onHandleCompleteAndRemovePlayers
                    }
                    onHandleSolveAgain={onHandleSolveAgain}
                    hideGearIcon
                    handleUncompleteSBC={handleUncompleteSBC}
                    isSolving={solutionStatus === "pending"}
                  />
                </div>
              </div>
            </div>
          )}
          <PitchView
            pitchRef={pitchRef}
            sbc={sbc}
            sbcFormation={sbc?.formation}
            playersToRender={playersToRender}
            isMobile={isMobile}
            isTouch={isTouch}
            smallDesktop={smallDesktop}
            widthClass={widthClass}
            selectedPlayer={selectedPlayer}
            selectedPlayerSwap={selectedPlayerSwap}
            playerSwaps={playerSwaps}
            loadingSwaps={loadingSwaps}
            handlePlayerClicked={handlePlayerClicked}
            handleSwapClicked={handleSwapClicked}
            onSwapClicked={onSwapClicked}
          />

          {isMobile && (
            <SolutionSummary
              rating={solution?.rating}
              chemistry={solution?.chem}
              owned={solution?.ownedPlayersCount}
              untradeables={solution?.untradeablesCount}
              duplicates={solution?.duplicatesCount}
              isMobile={isMobile}
            />
          )}
          {isMobile && (
            <CompleteAndSolveAgain
              isCompleted={sbc?.isCompleted}
              isUsingClubImport={solution?.ownedPlayersCount > 0}
              onHandleRemoveAndCompletePlayers={
                onHandleCompleteAndRemovePlayers
              }
              onHandleSolveAgain={onHandleSolveAgain}
              onToggleFiltersBar={() => setShowFiltersBar((v) => !v)}
              handleUncompleteSBC={handleUncompleteSBC}
              isSolving={solutionStatus === "pending"}
            />
          )}
        </div>
        {showDesktopSidebar && (
          <div className="shrink-0 z-20">
            <SBCFiltersSidebar
              showReward
              isMobile={isMobile}
              onHandleImportClicked={() =>
                initImport().then(() => {
                  dispatch(setIsCurrentlyImporting(true));
                  window.open(
                    "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
                    "_blank",
                  );
                  setTimeout(() => {
                    setShowImporting(true);
                  }, 1000);
                })
              }
              sbc={sbc}
              rewardImageUrl={sbcSetUrl}
              onHandleSolveAgain={onHandleSolveAgain}
              onHandleRemoveAndCompletePlayers={
                onHandleCompleteAndRemovePlayers
              }
              isUsingClubImport={solution?.ownedPlayersCount > 0}
              isOnSolutionsViewPage
            />
            {showImporting && (
              <NowImporting
                onClickDone={() => setShowImporting(false)}
                onClickCancel={() => setShowImporting(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SolutionView;
